import { useTranslation } from 'react-i18next';
import { formatBookingDateTimeRange, getCalendarProviderLabel } from '../../../utils/bookings';
import { resolveApiUrl } from '../../../services/api';
import type { BookingCreateResponse, ConsultationService } from '../../../types';

interface BookingStepDoneProps {
  service: ConsultationService;
  result: BookingCreateResponse;
  onClose: () => void;
}

export function BookingStepDone({ service, result, onClose }: BookingStepDoneProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="booking-success-state">
      <div className="booking-success-icon" aria-hidden>
        <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <span className="booking-status-pill status-requested">{t('booking.status.requested')}</span>
      <h3 className="booking-success-title">{t('booking.successTitle')}</h3>
      <p className="booking-success-copy">{t('booking.successCopy', { service: service.title })}</p>

      <div className="booking-success-grid">
        <div className="booking-success-item">
          <span>{t('booking.service')}</span>
          <strong>{result.serviceName}</strong>
        </div>
        <div className="booking-success-item">
          <span>{t('booking.schedule')}</span>
          <strong>{formatBookingDateTimeRange(result.startAt, result.endAt, i18n.resolvedLanguage)}</strong>
        </div>
        <div className="booking-success-item">
          <span>{t('booking.timezone')}</span>
          <strong>{result.timezone}</strong>
        </div>
        <div className="booking-success-item">
          <span>{t('booking.preferredCalendar')}</span>
          <strong>{getCalendarProviderLabel(t, result.calendarProvider)}</strong>
        </div>
        <div className="booking-success-item">
          <span>{t('booking.meetingLink') || 'Meeting Link'}</span>
          <strong style={{ wordBreak: 'break-all' }}>
            {result.meetingLink ? (
              <a
                href={result.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="booking-meeting-link"
                style={{ color: '#9c2426', textDecoration: 'underline' }}
              >
                {result.meetingLink}
              </a>
            ) : (
              'N/A'
            )}
          </strong>
        </div>
      </div>

      <p className="booking-pending-note">{t('booking.pendingNote')}</p>

      <div className="booking-calendar-actions">
        {result.calendarOptions.map((option) => {
          const label = option.primary
            ? t('booking.calendarPrimaryAction', { provider: getCalendarProviderLabel(t, option.provider) })
            : t('booking.calendarSecondaryAction', { provider: getCalendarProviderLabel(t, option.provider) });

          return (
            <a
              key={option.provider}
              className={`booking-calendar-link ${option.primary ? 'primary' : ''}`}
              href={resolveApiUrl(option.url)}
              target={option.download ? undefined : '_blank'}
              rel={option.download ? undefined : 'noreferrer'}
              download={option.download ? `fcs-booking-${result.id}.ics` : undefined}
            >
              {label}
            </a>
          );
        })}
      </div>

      <div className="booking-modal-actions">
        <button type="button" className="btn btn-filled" onClick={onClose}>
          {t('booking.close')}
        </button>
      </div>
    </div>
  );
}
